import {createSelector} from '@reduxjs/toolkit'

import {reduceValueKeys} from '@shared/helpers/object'

import {CouponDiscountType, CouponProviderType, DiscountCouponType} from '@web/core/constants'
import {deliveryGroupSelector} from '@web/slices/deliveryGroup/slice'
import {
    deliveryCouponInfoSelector,
    deliveryDiscountInfoSelector,
    duplicateCouponInfoSelector,
    immediateCouponInfoSelector,
    OrderSheetCoupon,
    orderSheetCouponSelector,
    productCouponInfoSelector,
    productItemDiscountInfoSelector,
    storeCouponInfoSelector,
} from '@web/slices/orderSheetCoupon/slice'
import {orderSheetProductInfoSelector} from '@web/slices/orderSheetProducts/slice'
import {orderAmountInfoSelector} from '@web/slices/orderTotal/slice'

import {
    getDeliveryCouponDiscount,
    getDeliveryDiscount,
    getDuplicatedProductCoupon,
    getMerchantAndManagerCoupons,
    getMerchantAndMangerCouponDiscount,
    getMerchantOrderAmount,
    getStoreCouponDiscount,
    getStoreCouponDiscountMap,
} from './utils/coupon'

/** 쿠폰적용 할인 정보 selector */
export const discountInfoSelector = createSelector(
    orderSheetProductInfoSelector,
    orderAmountInfoSelector,
    productItemDiscountInfoSelector,
    deliveryDiscountInfoSelector,
    deliveryGroupSelector,
    deliveryCouponInfoSelector,
    storeCouponInfoSelector,
    (
        {productsByMerchantNo},
        {orderAmount: orderAmountByDisAmount},
        {
            discountAmount: productDiscountAmount,
            productCouponDiscountAmountByItemId,
            multiPurchaseInfo,
            storeDiscountAmountByItemId,
        },
        deliveryDiscountInfo,
        {deliveryGroupsByKey},
        {selectedCoupons: selectedDeliveryCoupon},
        {coupons: storeCoupons},
    ) => {
        const merchantOrderAmount = getMerchantOrderAmount({
            productsByMerchantNo,
            productCouponDiscountAmountByItemId,
            storeCoupons,
        })

        const {deliveryDiscount, deliveryDiscountAmount} = (function () {
            if (deliveryDiscountInfo) {
                return {
                    deliveryDiscount: deliveryDiscountInfo.discountAmountByDeliveryGroupKey,
                    deliveryDiscountAmount: deliveryDiscountInfo.discountAmount,
                }
            }
            const tempDeliveryDiscount = getDeliveryDiscount({
                deliveryCoupons: selectedDeliveryCoupon,
                deliveryGroupsByKey,
            })

            const tempDeliveryDiscountAmount = reduceValueKeys((acc, value) => acc + value, 0, tempDeliveryDiscount)
            return {
                deliveryDiscount: tempDeliveryDiscount,
                deliveryDiscountAmount: tempDeliveryDiscountAmount,
            }
        })()

        const totalDiscountAmount = productDiscountAmount + deliveryDiscountAmount

        return {
            productDiscountItem: multiPurchaseInfo.productDiscountAmountByItemId,
            multiPurchaseDiscountAmountByItemId: multiPurchaseInfo.discountAmountByItemId,
            deliveryDiscount,
            merchantOrderAmount,
            totalProductDiscountAmount: productDiscountAmount,
            totalDeliveryDiscountAmount: deliveryDiscountAmount,
            totalDiscountAmount,
            orderAmountByDisAmount,
            storeDiscountAmountByItemId,
        }
    },
)

/** 스토어 쿠폰 info selector */
export const storeCouponDiscountInfoSelector = createSelector(
    storeCouponInfoSelector,
    discountInfoSelector,
    ({coupons: storeCoupons, selectedCoupons}, {merchantOrderAmount}) => {
        const {storeCouponDiscountMap, hasSelectedDisableCoupon, filteredStoreCouponsByDisabled} =
            getStoreCouponDiscountMap({
                storeCoupons,
                selectedStoreCoupons: selectedCoupons,
                merchantOrderAmount,
            })

        return {
            storeCouponDiscountMap,
            /** 선택된 쿠폰중 사용불가 쿠폰 존재 여부 */
            hasSelectedDisableCoupon,
            /** 선택된 쿠폰중 사용불가 쿠폰 제거 목록 */
            filteredStoreCouponsByDisabled,
        }
    },
)

type RefinedOrderSheetCoupon = OrderSheetCoupon & {discountAmount?: number}
interface CouponDiscountInfoByKey {
    [key: string]: {
        coupons: RefinedOrderSheetCoupon[]
        selectedCoupon: Maybe<RefinedOrderSheetCoupon>
    }
}

interface CouponInfoSelectorReturnType {
    merchantProductCouponDiscount: CouponDiscountInfoByKey
    duplicatedProductCouponDiscount: CouponDiscountInfoByKey
    managerProductCouponDiscount: CouponDiscountInfoByKey
    multiPurchaseDiscount: {[key: string]: number}
    deliveryCouponDiscount: CouponDiscountInfoByKey
    storeCouponDiscount: CouponDiscountInfoByKey
}

/** BE에서 받아온 선택된 쿠폰의 실 할인액을 리턴하는 함수 */
const makeSelectedCouponDiscountAmount =
    (productDiscountAmountInfoByItemId: {
        [OrderSheetItem: string]: {
            /** 적용 금액 */
            baseAmount: number
            /** 할인된 금액 */
            discountAmount: number
            /** 할인 유형 */
            discountType: CouponDiscountType
            /** 즉시할인 발행처 유형 */
            discountProviderType: CouponProviderType
            /** FE 쿠폰 구분 Type */
            couponType: DiscountCouponType
        }[]
    }) =>
    (itemId: string | number, couponTypeCheckFunction: (couponType: DiscountCouponType) => boolean): number => {
        return (
            productDiscountAmountInfoByItemId[itemId]?.find(({couponType}) => couponTypeCheckFunction(couponType))
                ?.discountAmount ?? 0
        )
    }

const duplicatedProductCouponDiscountSelector = createSelector(
    duplicateCouponInfoSelector,
    orderSheetCouponSelector,
    (duplicatedCouponInfo, {discount: {productItem}}) => {
        const getSelectedCouponDiscountAmount = makeSelectedCouponDiscountAmount(productItem)

        const {coupons: duplicatedCouponsByItemId, selectedCoupons: selectedDuplicatedCouponsByItemId} =
            duplicatedCouponInfo

        /** 상품 추가 할인 */
        return getDuplicatedProductCoupon({
            duplicatedCouponsByItemId,
            selectedDuplicatedCouponsByItemId,
            getSelectedCouponDiscountAmount,
        })
    },
)

const deliveryCouponDiscountSelector = createSelector(
    deliveryCouponInfoSelector,
    orderSheetCouponSelector,
    (deliveryCouponInfo, {discount: {deliveryItem}}) => {
        /** 배송비 쿠폰 */
        const {coupons: deliveryCouponByDeliveryGroupKey, selectedCoupons: selectedDeliveryCouponByDeliveryGroupKey} =
            deliveryCouponInfo

        return getDeliveryCouponDiscount({
            deliveryCouponByDeliveryGroupKey,
            selectedDeliveryCouponByDeliveryGroupKey,
            deliveryItem,
        })
    },
)

const storeCouponDiscountSelector = createSelector(
    storeCouponInfoSelector,
    orderSheetCouponSelector,
    orderSheetProductInfoSelector,
    storeCouponDiscountInfoSelector,
    (
        storeCouponInfo,
        {discount: {productItem: productDiscountAmountInfoByItemId}},
        {productsByMerchantNo},
        {storeCouponDiscountMap},
    ) => {
        const getSelectedCouponDiscountAmount = makeSelectedCouponDiscountAmount(productDiscountAmountInfoByItemId)

        /** 스토어 쿠폰 */
        const {selectedCoupons: selectedStoreCouponByMerchantNo} = storeCouponInfo

        return getStoreCouponDiscount({
            getSelectedCouponDiscountAmount,
            productsByMerchantNo,
            storeCouponDiscountMap,
            selectedStoreCouponByMerchantNo,
        })
    },
)

export const immediateCouponContainedCouponInfoSelector = createSelector(
    productCouponInfoSelector,
    immediateCouponInfoSelector,
    orderSheetCouponSelector,
    orderSheetProductInfoSelector,
    (productCouponInfo, immediateCouponInfo, {discount: {productItem}}, {orderSheetItemFlatMap}) => {
        const getSelectedCouponDiscountAmount = makeSelectedCouponDiscountAmount(productItem)

        const {coupons: productCouponsByItemId, selectedCoupons: selectedProductCouponsByItemId} = productCouponInfo
        const {coupons: immediatedCouponsByItemId, selectedCoupons: selectedImmediateCouponsByItemId} =
            immediateCouponInfo

        const result = getMerchantAndManagerCoupons({
            productCouponsByItemId,
            selectedProductCouponsByItemId,
            immediatedCouponsByItemId,
            selectedImmediateCouponsByItemId,
            orderSheetItemFlatMap,
            getSelectedCouponDiscountAmount,
        })
        /** itemId 별 merchantCouponDiscount / managerCouponDiscount 정보 */
        return result
    },
)

/**
 * UI 개선 이후 상품 영역에 사용할 coupon 정보 통합 selector
 */
export const couponInfoSelector = createSelector(
    discountInfoSelector,
    immediateCouponContainedCouponInfoSelector,
    duplicatedProductCouponDiscountSelector,
    deliveryCouponDiscountSelector,
    storeCouponDiscountSelector,
    (
        {multiPurchaseDiscountAmountByItemId},
        immediateCouponContainedCouponInfoByItemId,
        duplicatedProductCouponDiscount,
        deliveryCouponDiscount,
        storeCouponDiscount,
    ): CouponInfoSelectorReturnType => {
        const {merchantProductCouponDiscount, managerProductCouponDiscount} = getMerchantAndMangerCouponDiscount({
            immediateCouponContainedCouponInfoByItemId,
        })

        return {
            /** 판매자 상품 할인 */
            merchantProductCouponDiscount,
            /** 상품 추가 할인 */
            duplicatedProductCouponDiscount,
            /** 네이버 추가할인 */
            managerProductCouponDiscount,
            /** 복수구매 할인 */
            multiPurchaseDiscount: multiPurchaseDiscountAmountByItemId,
            /** 배송비 쿠폰 */
            deliveryCouponDiscount,
            /** 스토어 쿠폰 */
            storeCouponDiscount,
        }
    },
)
